import type { ComputedMetrics } from "@/types/metrics";
import type { FilterState } from "@/types/filters";
import { KpiCard } from "@/components/dashboard/KpiCard";
import { FormulaCardRow } from "@/components/dashboard/FormulaCardRow";
import { METRIC_TOOLTIPS } from "@/lib/metricTooltips";

interface CapacityCardsProps {
  current: ComputedMetrics;
  priorYear: ComputedMetrics;
  nvaAvg: ComputedMetrics;
  filters: FilterState;
  siteCount: number;
  nvaSiteCount: number;
}

export function CapacityCards({
  current,
  priorYear,
  nvaAvg,
  filters,
  siteCount,
  nvaSiteCount,
}: CapacityCardsProps) {
  const sites = Math.max(1, siteCount);
  const nvaSites = Math.max(1, nvaSiteCount);
  const { capacityView } = filters;

  return (
    <div className="mb-7">
      {capacityView === "avg" ? (
        <FormulaCardRow
          items={[
            {
              key: "perWeek",
              card: (
                <KpiCard
                  label="Avg. hours per DVM per week"
                  unit="hours"
                  current={current.avgHoursPerDvmPerWeek}
                  priorYear={priorYear.avgHoursPerDvmPerWeek}
                  nva={nvaAvg.avgHoursPerDvmPerWeek}
                  deltaUnit="h"
                  compactDeltas
                  definition={METRIC_TOOLTIPS.hoursPerDvmPerWeek}
                />
              ),
            },
            {
              key: "perDay",
              op: "=",
              card: (
                <KpiCard
                  label="Avg. hours per DVM per day"
                  unit="hours"
                  current={current.avgHoursPerDay}
                  priorYear={priorYear.avgHoursPerDay}
                  nva={nvaAvg.avgHoursPerDay}
                  deltaUnit="h"
                  compactDeltas
                  definition={METRIC_TOOLTIPS.hoursPerDvmPerDay}
                />
              ),
            },
            {
              key: "daysWorked",
              op: "×",
              card: (
                <KpiCard
                  label="Days per DVM per week"
                  unit="days"
                  current={current.daysPerDvmPerWeek}
                  priorYear={priorYear.daysPerDvmPerWeek}
                  nva={nvaAvg.daysPerDvmPerWeek}
                  deltaUnit="d"
                  definition={METRIC_TOOLTIPS.daysPerDvmPerWeek}
                  compactDeltas
                />
              ),
            },
            // Not a term of the equation -- it multiplies nothing and is
            // multiplied by nothing -- so it takes the gutter without an
            // operator in it rather than reading as part of the product.
            {
              key: "dvmsPerSite",
              spacer: true,
              card: (
                <KpiCard
                  label="DVMs per day per site"
                  unit="DVMs"
                  current={current.dvmsPerDay / sites}
                  priorYear={priorYear.dvmsPerDay / sites}
                  nva={nvaAvg.dvmsPerDay / nvaSites}
                  deltaUnit=""
                  definition={METRIC_TOOLTIPS.dvmsPerDayPerSite}
                  compactDeltas
                />
              ),
            },
          ]}
        />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <KpiCard
            label="Total DVM hours per week"
            unit="hours"
            current={current.avgHoursPerWeekTotal}
            priorYear={priorYear.avgHoursPerWeekTotal}
            deltaUnit="h"
            definition={METRIC_TOOLTIPS.totalHoursPerWeek}
            displayValue={Math.round(current.avgHoursPerWeekTotal).toLocaleString()}
          />
          <KpiCard
            label="Total DVM hours over selected period"
            unit="hours"
            current={current.dvmHours}
            priorYear={priorYear.dvmHours}
            deltaUnit="h"
            definition={METRIC_TOOLTIPS.totalHours}
            displayValue={Math.round(current.dvmHours).toLocaleString()}
          />
        </div>
      )}
    </div>
  );
}
